import { useRef, useState, type ChangeEvent, type KeyboardEvent, type SyntheticEvent } from 'react'
import {
  CalendarDaysIcon,
  CopyPlusIcon,
  EllipsisVerticalIcon,
  MinusIcon,
  Trash2Icon
} from 'lucide-react'
import {
  clockDurationMinutes,
  dateFromString,
  dateString,
  formatDuration,
  parseClockTime,
  type CreateTimeEntryInput,
  type Person,
  type Project,
  type Task,
  type TimeEntry
} from '@coneno/c-sheet-shared'
import { AppSelect } from '@/components/forms/app-select'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import type { EntryDraft } from '../hooks/use-entry-draft'

type EntryFormProps = {
  draft: EntryDraft
  people: Person[] | undefined
  projects: Project[] | undefined
  /** Tasks of the draft's project. */
  tasks: Task[] | undefined
  customerNames: Map<string, string>
  taskNames: Map<string, string>
  /** Earlier descriptions to browse with the arrow keys, newest first. */
  descriptions: string[]
  /** The entry being edited, when it is still in the current week. */
  editingEntry: TimeEntry | undefined
  isSaving: boolean
  deleting: boolean
  onPersonChange(id: string): void
  onSubmit(input: CreateTimeEntryInput): void
  onClone(entry: TimeEntry): void
  onDelete(entry: TimeEntry): void
}

const clockTimeInputClass =
  'w-18 px-1 py-0 text-center text-base font-semibold tabular-nums md:text-base'

/** The quick-entry popover: times, project and task, description, then date, person and billable. */
export function EntryForm({
  draft,
  people,
  projects,
  tasks,
  customerNames,
  taskNames,
  descriptions,
  editingEntry,
  isSaving,
  deleting,
  onPersonChange,
  onSubmit,
  onClone,
  onDelete
}: EntryFormProps): React.JSX.Element {
  const formRef = useRef<HTMLFormElement>(null)
  const startTimeRef = useRef<HTMLInputElement>(null)
  const endTimeRef = useRef<HTMLInputElement>(null)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const selectedProject = projects?.find((project) => project.id === draft.projectId)
  const selectedCustomerName = selectedProject?.customerId
    ? customerNames.get(selectedProject.customerId)
    : undefined

  function handleClockTimeChange(event: ChangeEvent<HTMLInputElement>): void {
    const input = event.currentTarget
    const isStartTime = input.id === 'entry-start-time'
    if (isStartTime) draft.setStartTime(input.value)
    else draft.setEndTime(input.value)

    if (
      (event.nativeEvent as InputEvent).isComposing ||
      !/^(?:\d{4}|\d{2}:\d{2})$/.test(input.value) ||
      !parseClockTime(input.value) ||
      input.selectionStart !== input.value.length ||
      input.selectionEnd !== input.value.length ||
      (!isStartTime && !clockDurationMinutes(draft.startTime, input.value))
    ) {
      return
    }

    if (isStartTime) endTimeRef.current?.focus()
    else {
      document
        .getElementById(draft.projectPrefilled ? 'entry-description' : 'entry-project')
        ?.focus()
    }
  }

  function handleClockTimeBlur(event: React.FocusEvent<HTMLInputElement>): void {
    const normalized = parseClockTime(event.currentTarget.value)
    if (normalized) {
      if (event.currentTarget.id === 'entry-start-time') draft.setStartTime(normalized)
      else draft.setEndTime(normalized)
    }
    const nextId = (event.relatedTarget as HTMLElement | null)?.id
    if (nextId !== 'entry-start-time' && nextId !== 'entry-end-time') {
      draft.setClockValidationEnabled(true)
    }
  }

  // Backspace at the start of the end time continues deleting in the start time.
  function handleEndTimeKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (
      event.key !== 'Backspace' ||
      event.defaultPrevented ||
      event.nativeEvent.isComposing ||
      event.shiftKey ||
      event.ctrlKey ||
      event.altKey ||
      event.metaKey ||
      event.currentTarget.selectionStart !== 0 ||
      event.currentTarget.selectionEnd !== 0 ||
      isSaving
    ) {
      return
    }

    const input = startTimeRef.current
    if (!input) return
    event.preventDefault()
    const value = draft.startTime.slice(0, -1)
    input.focus()
    draft.setStartTime(value)
    // Restore the caret after focus selects the value and React updates the input.
    requestAnimationFrame(() => {
      if (document.activeElement === input) input.setSelectionRange(value.length, value.length)
    })
  }

  function handleDescriptionKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (
      (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') ||
      event.nativeEvent.isComposing ||
      event.shiftKey ||
      event.ctrlKey ||
      event.altKey ||
      event.metaKey ||
      isSaving
    ) {
      return
    }
    if (draft.browseDescriptions(event.key, descriptions)) event.preventDefault()
  }

  function navigateField(id: string, direction: -1 | 1): void {
    const fields = Array.from(
      formRef.current?.querySelectorAll<HTMLElement>(
        'input[id^="entry-"]:not([type="hidden"]):not([aria-hidden="true"]):not(:disabled), button[id^="entry-"]:not(#entry-submit):not(:disabled)'
      ) ?? []
    )
    const index = fields.findIndex((field) => field.id === id)
    if (index === -1 || isSaving) return
    const nextField = fields[(index + direction + fields.length) % fields.length]
    nextField.focus({ preventScroll: true })
    if (nextField instanceof HTMLInputElement && nextField.type === 'text') {
      const caret = direction === -1 ? nextField.value.length : 0
      nextField.setSelectionRange(caret, caret)
    }
  }

  // Left and right at the edge of a field move to the neighbouring field; Enter moves on or submits.
  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>): void {
    if (
      event.defaultPrevented ||
      event.nativeEvent.isComposing ||
      event.shiftKey ||
      event.ctrlKey ||
      event.altKey ||
      event.metaKey ||
      !(event.target instanceof HTMLElement) ||
      !event.target.id.startsWith('entry-')
    ) {
      return
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const field = event.target
      if (field.id === 'entry-submit') return
      if (field instanceof HTMLInputElement) {
        const edge = event.key === 'ArrowLeft' ? 0 : field.value.length
        if (field.selectionStart !== edge || field.selectionEnd !== edge) return
      } else if (!(field instanceof HTMLButtonElement)) {
        return
      }
      event.preventDefault()
      navigateField(field.id, event.key === 'ArrowLeft' ? -1 : 1)
      return
    }

    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return

    event.preventDefault()
    if (event.repeat || isSaving) return

    if (event.target.id === 'entry-description') {
      event.currentTarget.requestSubmit()
      return
    }

    const fields = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'input[id^="entry-"]:not(:disabled), button[id^="entry-"]:not(:disabled)'
      )
    )
    fields[fields.indexOf(event.target) + 1]?.focus()
  }

  function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault()
    const startTime = parseClockTime(draft.startTime)
    const endTime = parseClockTime(draft.endTime)
    if (
      !draft.effectivePersonId ||
      !draft.projectId ||
      !draft.durationMinutes ||
      !startTime ||
      !endTime
    ) {
      draft.setClockValidationEnabled(true)
      return
    }
    onSubmit({
      personId: draft.effectivePersonId,
      projectId: draft.projectId,
      taskId: draft.taskId || null,
      workDate: draft.workDate,
      startTime,
      endTime,
      description: draft.description.trim() || null,
      billable: draft.billable
    })
  }

  return (
    <PopoverContent
      id="entry-form"
      anchor={draft.anchor}
      initialFocus={() => document.querySelector<HTMLInputElement>('#entry-start-time')}
      className="w-[min(50rem,calc(100vw-2rem))] max-w-(--available-width) p-3"
      align="center"
      side="bottom"
    >
      <PopoverHeader className="sr-only">
        <PopoverTitle>{draft.editingId ? 'Edit entry' : 'Quick entry'}</PopoverTitle>
        <PopoverDescription>Enter the time, project, and optional task.</PopoverDescription>
      </PopoverHeader>
      <form
        ref={formRef}
        className="flex flex-col gap-0"
        onSubmit={submit}
        onKeyDown={handleKeyDown}
      >
        <FieldGroup className="-ml-2.5 w-[calc(100%+0.625rem)]">
          <Field
            orientation="horizontal"
            className="min-w-0 flex-wrap gap-3"
            data-invalid={Boolean(draft.visibleClockTimeError)}
            data-disabled={isSaving}
          >
            <div className="flex shrink-0 items-center gap-1.5">
              <FieldLabel className="sr-only" htmlFor="entry-start-time">
                Start time
              </FieldLabel>
              <Input
                id="entry-start-time"
                ref={startTimeRef}
                variant="quiet"
                className={clockTimeInputClass}
                value={draft.startTime}
                onFocus={(event) => {
                  draft.setClockValidationEnabled(false)
                  event.currentTarget.select()
                }}
                onChange={handleClockTimeChange}
                onBlur={handleClockTimeBlur}
                placeholder="08:30"
                inputMode="numeric"
                maxLength={5}
                aria-invalid={Boolean(
                  draft.clockValidationEnabled && (draft.startTimeError || draft.timeRangeError)
                )}
                aria-describedby={draft.visibleClockTimeError ? 'entry-time-error' : undefined}
                disabled={isSaving}
              />
              <MinusIcon aria-hidden="true" className="size-4 text-muted-foreground" />
              <FieldLabel className="sr-only" htmlFor="entry-end-time">
                End time
              </FieldLabel>
              <Input
                id="entry-end-time"
                ref={endTimeRef}
                variant="quiet"
                className={clockTimeInputClass}
                value={draft.endTime}
                onFocus={(event) => {
                  draft.setClockValidationEnabled(false)
                  event.currentTarget.select()
                }}
                onChange={handleClockTimeChange}
                onKeyDown={handleEndTimeKeyDown}
                onBlur={handleClockTimeBlur}
                placeholder="17:00"
                inputMode="numeric"
                maxLength={5}
                aria-invalid={Boolean(
                  draft.clockValidationEnabled && (draft.endTimeError || draft.timeRangeError)
                )}
                aria-describedby={draft.visibleClockTimeError ? 'entry-time-error' : undefined}
                disabled={isSaving}
              />
            </div>
            {draft.visibleClockTimeError && (
              <FieldError
                id="entry-time-error"
                className="min-w-0 flex-1 group-focus-within/field:hidden"
              >
                {draft.visibleClockTimeError}
              </FieldError>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-1.5 tabular-nums">
              <span className="text-muted-foreground">Duration</span>
              <span className="font-medium">
                {draft.durationMinutes ? formatDuration(draft.durationMinutes) : '—'}
              </span>
            </div>
          </Field>
        </FieldGroup>

        <FieldGroup className="-ml-2.5 grid w-[calc(100%+0.625rem)] grid-cols-1 gap-2 pb-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <Field orientation="horizontal" className="min-w-0 flex-wrap gap-1.5">
            <FieldLabel className="sr-only" htmlFor="entry-project">
              Project
            </FieldLabel>
            <AppSelect
              id="entry-project"
              value={draft.projectId}
              onValueChange={draft.selectProject}
              focusAfterSelectionId="entry-task"
              onArrowNavigate={(direction) => navigateField('entry-project', direction)}
              disabled={isSaving}
              variant="link"
              triggerLabel={selectedProject?.name ?? 'Select a project'}
              triggerClassName="w-auto max-w-56 pl-2.5"
              options={[
                { value: '', label: 'Select a project' },
                ...(projects
                  ?.filter((project) => project.active || project.id === draft.projectId)
                  .map((project) => ({
                    value: project.id,
                    label: `${project.customerId ? `${customerNames.get(project.customerId) ?? 'Client'} / ` : ''}${project.name}`
                  })) ?? [])
              ]}
            />
            {draft.projectId && <span className="text-muted-foreground">:</span>}
            <FieldLabel className="sr-only" htmlFor="entry-task">
              Task (optional)
            </FieldLabel>
            <AppSelect
              id="entry-task"
              value={draft.taskId}
              onValueChange={draft.setTaskId}
              focusAfterSelectionId="entry-description"
              onArrowNavigate={(direction) => navigateField('entry-task', direction)}
              disabled={!draft.projectId || isSaving}
              variant="link"
              triggerLabel={taskNames.get(draft.taskId) ?? 'No task'}
              triggerClassName="w-auto max-w-52"
              options={[
                { value: '', label: 'No task' },
                ...(tasks
                  ?.filter((task) => task.active || task.id === draft.taskId)
                  .map((task) => ({ value: task.id, label: task.name })) ?? [])
              ]}
            />
            {selectedCustomerName && (
              <span className="-ml-1 inline-flex min-w-0 max-w-full gap-1.5 text-muted-foreground">
                <span aria-hidden="true">–</span>
                <span className="truncate">{selectedCustomerName}</span>
              </span>
            )}
          </Field>
          <Field className="min-w-0">
            <FieldLabel className="sr-only" htmlFor="entry-description">
              Description (optional)
            </FieldLabel>
            <Input
              id="entry-description"
              variant="quiet"
              value={draft.description}
              onChange={(event) => draft.setDescription(event.target.value)}
              onKeyDown={handleDescriptionKeyDown}
              aria-describedby="entry-description-history"
              title="Use ↑ and ↓ to browse previous descriptions from all entries"
              placeholder="Describe the work briefly"
              disabled={isSaving}
            />
            <span id="entry-description-history" className="sr-only">
              Use Up and Down arrow keys to browse previous descriptions from all entries. Down
              after the newest description restores your draft.
            </span>
          </Field>
        </FieldGroup>

        <div className="-mx-3 -mb-3 rounded-b-lg bg-muted/40">
          <Separator />
          <FieldGroup className="w-auto flex-row flex-wrap items-center gap-2 px-3 py-2.5">
            <Field orientation="horizontal" className="w-auto">
              <FieldLabel className="sr-only" htmlFor="entry-date-trigger">
                Date
              </FieldLabel>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger
                  render={
                    <Button
                      id="entry-date-trigger"
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-start font-normal"
                      disabled={isSaving}
                    />
                  }
                >
                  <CalendarDaysIcon data-icon="inline-start" />
                  {new Intl.DateTimeFormat(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }).format(dateFromString(draft.workDate))}
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFromString(draft.workDate)}
                    onSelect={(date) => {
                      if (!date) return
                      draft.setWorkDate(dateString(date))
                      setDatePickerOpen(false)
                    }}
                    defaultMonth={dateFromString(draft.workDate)}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
            <Field orientation="horizontal" className="w-auto">
              <FieldLabel className="sr-only" htmlFor="entry-person">
                Person
              </FieldLabel>
              <AppSelect
                id="entry-person"
                ariaLabel="Person"
                onArrowNavigate={(direction) => navigateField('entry-person', direction)}
                value={draft.effectivePersonId}
                onValueChange={onPersonChange}
                disabled={isSaving}
                size="sm"
                triggerClassName="w-auto max-w-44"
                options={[
                  { value: '', label: 'Select a person' },
                  ...(people
                    ?.filter((person) => person.active || person.id === draft.effectivePersonId)
                    .map((person) => ({
                      value: person.id,
                      label: `${person.name}${person.active ? '' : ' (inactive)'}`
                    })) ?? [])
                ]}
              />
            </Field>
            <Field orientation="horizontal" className="w-auto" data-disabled={isSaving}>
              <Switch
                id="entry-billable"
                nativeButton
                render={<button type="button" />}
                checked={draft.billable}
                onCheckedChange={draft.setBillable}
                disabled={isSaving}
              />
              <FieldLabel htmlFor="entry-billable">Billable</FieldLabel>
            </Field>
            <Field orientation="horizontal" className="ml-auto w-auto justify-end gap-2">
              <FieldLabel className="sr-only" htmlFor="entry-submit">
                {draft.editingId ? 'Save entry' : 'Add entry'}
              </FieldLabel>
              <Button type="button" variant="ghost" onClick={draft.reset} disabled={isSaving}>
                Cancel
              </Button>
              {editingEntry && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label="Entry actions"
                      />
                    }
                  >
                    <EllipsisVerticalIcon />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-40">
                    <DropdownMenuGroup>
                      <DropdownMenuItem onClick={() => onClone(editingEntry)}>
                        <CopyPlusIcon />
                        Clone entry to…
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        disabled={deleting}
                        onClick={() => onDelete(editingEntry)}
                      >
                        <Trash2Icon />
                        Delete entry
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                id="entry-submit"
                type="submit"
                disabled={
                  isSaving || !draft.effectivePersonId || !draft.projectId || !draft.durationMinutes
                }
              >
                {isSaving ? 'Saving…' : draft.editingId ? 'Save entry' : 'Add entry'}
              </Button>
            </Field>
          </FieldGroup>
        </div>
      </form>
    </PopoverContent>
  )
}
